import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import propertyService from '../services/propertyService';
import AddPropertyAutoComplete from '../components/AddPropertyAutoComplete';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Home, Search, Plus, Calendar, Filter, ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { PropertyRegistryStub } from '../models/property';

// Simple input component
const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return (
    <input
      ref={ref}
      className={`flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  )
})
Input.displayName = "Input"

const PropertyListPage: React.FC = () => {
  const [properties, setProperties] = useState<PropertyRegistryStub[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<PropertyRegistryStub[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  
  // Sort options
  const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'alphabetical'>('newest');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Pagination options
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  const pageSizeDropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Handle sort dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      
      // Handle page size dropdown
      if (pageSizeDropdownRef.current && !pageSizeDropdownRef.current.contains(event.target as Node)) {
        setPageSizeDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // For testing purposes - temporary fake data
  const fakeDemoProperties = [
    {
      id: '1234',
      address: '123 Main Street, San Francisco, CA 94103',
      date: new Date('2023-10-15')
    },
    {
      id: '5678',
      address: '456 Market Avenue, Oakland, CA 94611',
      date: new Date('2023-11-22')
    },
    {
      id: '9012',
      address: '789 Valencia Blvd, San Jose, CA 95113',
      date: new Date('2024-01-05')
    }
  ];

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const registry = await propertyService.getPropertyRegistry();
        console.log('Fetched property registry:', registry);
        
        if (registry && registry.propertyRegistry && registry.propertyRegistry.length > 0) {
          // Use real data if available
          setProperties(registry.propertyRegistry);
          setFilteredProperties(registry.propertyRegistry);
        } else {
          console.warn('Property registry is empty, using demo data for UI testing');
          // Use fake demo data for testing if no real data
          setProperties(fakeDemoProperties);
          setFilteredProperties(fakeDemoProperties);
        }
      } catch (error) {
        console.error('Error fetching property registry:', error);
        // Fall back to demo data on error
        setProperties(fakeDemoProperties);
        setFilteredProperties(fakeDemoProperties);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);


  // Handle both filtering and initial sorting
  useEffect(() => {
    // Skip if no properties yet
    if (properties.length === 0) return;
    
    // Step 1: Filter properties based on search query
    let filtered;
    if (searchQuery.trim() === '') {
      filtered = [...properties];
    } else {
      const lowerCaseQuery = searchQuery.toLowerCase();
      filtered = properties.filter(property => 
        property.address.toLowerCase().includes(lowerCaseQuery)
      );
    }
    
    // Step 2: Apply current sort option to filtered results
    switch (sortOption) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.address.localeCompare(b.address));
        break;
    }
    
    // Update state with filtered and sorted data
    setFilteredProperties(filtered);
  }, [searchQuery, properties, sortOption]);
  

  const handlePropertyClick = (propertyId: string) => {
    navigate(`/property/${propertyId}`);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex flex-col gap-6">
        {/* Header with Title and Add New Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-fxer-blue">My Properties</h1>
            <p className="text-gray-500 mt-1">Manage your properties and inspection reports</p>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-fxer-blue hover:opacity-90"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Property
          </Button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search properties..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="relative" ref={dropdownRef}>
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <Filter className="h-4 w-4" />
              <span>Sort: {sortOption === 'newest' ? 'Newest' : sortOption === 'oldest' ? 'Oldest' : 'A-Z'}</span>
            </Button>
            
            {dropdownOpen && (
              <div className="absolute right-0 z-10 mt-2 w-40 rounded-md border border-gray-200 bg-white shadow-lg">
                <div className="p-1">
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'newest' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('newest');
                      setDropdownOpen(false);
                    }}
                  >
                    Newest First
                  </button>
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'oldest' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('oldest');
                      setDropdownOpen(false);
                    }}
                  >
                    Oldest First
                  </button>
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'alphabetical' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('alphabetical');
                      setDropdownOpen(false);
                    }}
                  >
                    Alphabetical
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add New Property Form */}
        {showForm && (
          <Card>
            <CardContent className="pt-6">
              <AddPropertyAutoComplete
                onClose={() => setShowForm(false)}
                onSuccess={() => setShowForm(false)}
              />
            </CardContent>
          </Card>
        )}

        {/* Property List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-10 w-10 text-fxer-blue animate-spin mb-4" />
            <p className="text-gray-500">Loading properties...</p>
          </div>
        ) : filteredProperties.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              {searchQuery ? (
                <>
                  <p className="text-gray-500 mb-2">No properties match your search</p>
                  <Button variant="outline" onClick={() => setSearchQuery('')}>
                    Clear Search
                  </Button>
                </>
              ) : (
                <>
                  <Home className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium mb-2">No properties yet</h3>
                  <p className="text-gray-500 mb-6">
                    Add your first property to get started
                  </p>
                  <Button 
                    onClick={() => setShowForm(true)}
                    className="bg-fxer-blue hover:opacity-90"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Property
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Count display */}
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredProperties.length)} of {filteredProperties.length} properties
              </div>
              
              {/* Page size selector */}
              <div className="relative" ref={pageSizeDropdownRef}>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm"
                  onClick={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)}
                >
                  {pageSize} per page
                </Button>
                
                {pageSizeDropdownOpen && (
                  <div className="absolute right-0 z-10 mt-2 w-32 rounded-md border border-gray-200 bg-white shadow-lg">
                    <div className="p-1">
                      {[5, 10, 25, 50].map((size) => (
                        <button
                          key={size}
                          className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${pageSize === size ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                          onClick={() => {
                            setPageSize(size);
                            setCurrentPage(1); // Reset to first page when changing page size
                            setPageSizeDropdownOpen(false);
                          }}
                        >
                          {size} per page
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          
            {/* Property cards */}
            <div className="grid grid-cols-1 gap-4">
              {filteredProperties
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((property) => (
                  <Card 
                    key={property.id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handlePropertyClick(property.id)}
                  >
                    <CardContent className="p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-medium mb-1">{property.address}</h3>
                          <div className="flex items-center text-gray-500 text-sm">
                            <Calendar className="h-3.5 w-3.5 mr-1" />
                            <span>Added {new Date(property.date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          className="rounded-full h-8 w-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePropertyClick(property.id);
                          }}
                        >
                          <Home className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
            
            {/* Pagination controls */}
            {filteredProperties.length > pageSize && (
              <div className="flex items-center justify-center mt-8 space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  className="hidden sm:flex"
                >
                  First
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </Button>
                
                <div className="flex items-center">
                  {/* Page number buttons */}
                  {Array.from({ length: Math.min(5, Math.ceil(filteredProperties.length / pageSize)) }, (_, i) => {
                    const pageNum = i + 1;
                    // For simplicity, just show first 5 pages
                    // In a real app, you'd calculate visible page numbers around current page
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`mx-0.5 w-8 h-8 p-0 ${currentPage === pageNum ? 'bg-fxer-blue' : ''}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  {Math.ceil(filteredProperties.length / pageSize) > 5 && (
                    <span className="mx-1 text-gray-500">
                      <MoreHorizontal className="h-4 w-4" />
                    </span>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= Math.ceil(filteredProperties.length / pageSize)}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredProperties.length / pageSize)))}
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= Math.ceil(filteredProperties.length / pageSize)}
                  onClick={() => setCurrentPage(Math.ceil(filteredProperties.length / pageSize))}
                  className="hidden sm:flex"
                >
                  Last
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PropertyListPage;